"use client";

import { BookOpen, Menu, Search, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { BrandMark } from "@/components/brand-mark";
import { openCommandPalette } from "@/components/command-palette-event";
import { LanguageSelector } from "@/components/i18n/language-selector";
import Link from "@/components/locale-link";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { localePath, t } from "@/lib/i18n";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import type { SiteChromeDictionary } from "@/lib/i18n/site-chrome-dictionary";
import { cn } from "@/lib/utils";

/**
 * The site header and footer — one implementation, every language.
 *
 * These used to be two separate pairs: `components/header.tsx` + `components/footer.tsx`
 * for the English tree, and a hand-written localized pair in `components/i18n/`. They
 * drifted, which is exactly what you would expect of two components that are supposed
 * to render the same thing and share no code. So there is now one of each, driven by a
 * dictionary. Anything added to the chrome from here on appears in all 17 languages or
 * in none.
 *
 * Layout follows the pattern the best product sites converge on: wordmark at the
 * start, a short row of text links beside it, and at the end a cluster of icon-only
 * utilities (search, language, theme) ahead of the one primary action. The footer is a
 * brand column beside four link columns, over a slim legal bar.
 *
 * Both are client components because the header owns the bibliography button's saved
 * count and the mobile menu's open state, and the footer owns the language selector's
 * open state. Neither imports the site catalog — the style count arrives preformatted
 * from the server layout, because importing the catalog here would ship ~13,000
 * entries to the browser.
 */

interface ChromeLink {
  /** English site path. Localized links are prefixed per locale at render time. */
  href: string;
  label: string;
  /**
   * True when the destination exists in every language. False (the default) means the
   * page is English-only, and the link is marked `hreflang="en"` so both readers and
   * crawlers know before they click.
   */
  localized?: boolean;
}

/**
 * A chrome link, marked with its language when it leaves the reader's.
 *
 * The site translates 37 pages and has ~12,900 more in English only. Pretending
 * otherwise — by hiding the English pages from localized navigation — would make the
 * localized tree a dead end. Marking them is the honest option and the one Google
 * documents.
 */
function ChromeAnchor({
  locale,
  link,
  className,
  ariaCurrent,
  onClick,
}: {
  locale: string;
  link: ChromeLink;
  className: string;
  ariaCurrent?: "page";
  onClick?: () => void;
}) {
  const isLocalized = link.localized ?? false;
  const englishOnly = !isLocalized && locale !== DEFAULT_LOCALE;
  return (
    <Link
      aria-current={ariaCurrent}
      className={className}
      href={isLocalized ? localePath(locale, link.href) : link.href}
      hrefLang={englishOnly ? "en" : undefined}
      lang={englishOnly ? "en" : undefined}
      onClick={onClick}
    >
      {link.label}
    </Link>
  );
}

/**
 * The wordmark: a small mark plus the name, used by the header and the footer.
 *
 * `compact` drops the name to screen readers only on the narrowest phones, where the
 * header's five 40px touch targets leave the mark no room for it (see SiteHeader).
 */
function Wordmark({
  locale,
  compact = false,
}: {
  locale: string;
  compact?: boolean;
}) {
  return (
    <Link
      className="flex shrink-0 items-center gap-2.5 rounded-lg font-semibold text-foreground tracking-tight transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      href={localePath(locale, "/")}
    >
      <BrandMark />
      <span
        className={compact ? "sr-only min-[23.25rem]:not-sr-only" : undefined}
      >
        CitationEasy
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

/** Does the current URL live under this link? Marks the active section. */
function isActivePath(pathname: string, locale: string, link: ChromeLink) {
  const target = link.localized ? localePath(locale, link.href) : link.href;
  return pathname === target || pathname.startsWith(`${target}/`);
}

export function SiteHeader({
  locale,
  dict,
  englishPath,
  citationCount,
}: {
  locale: string;
  dict: SiteChromeDictionary;
  /** English path of the current page, so the language selector keeps the reader's place. */
  englishPath: string;
  citationCount: number;
}) {
  const pathname = usePathname() ?? `/${locale}`;
  const [menuOpen, setMenuOpen] = useState(false);

  // Navigating closes the menu. Comparing against the path the menu was opened on
  // does this during render, without an effect that fires after the old menu paints.
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    setMenuOpen(false);
  }

  // So does Escape.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const nav: ChromeLink[] = [
    { href: "/tools", label: dict.nav.tools, localized: true },
    { href: "/guides", label: dict.nav.guides, localized: true },
    { href: "/citation-styles", label: dict.nav.styles, localized: true },
    { href: "/how-to-cite", label: dict.footer.howToCiteAny, localized: true },
  ];

  const bibliographyHref = localePath(locale, "/bibliography");
  const badge =
    citationCount > 0 ? (
      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1.5 font-semibold text-[11px] tabular-nums">
        {citationCount}
      </span>
    ) : null;

  return (
    <header className="sticky top-0 z-50 w-full border-border/60 border-b bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
      <div className="site-container">
        {/* Below sm the row holds the wordmark (130px) and five 40px touch targets
            inside 16px gutters: 370px with the tighter gaps below, so the name shows
            from 23.25rem (372px) and the mark stands alone on narrower phones. */}
        <div className="flex h-14 items-center gap-2 sm:h-16 sm:gap-6">
          <Wordmark compact locale={locale} />

          {/* Primary navigation — text links, desktop only. */}
          <nav
            aria-label={dict.nav.menu}
            className="hidden items-center gap-1 md:flex"
          >
            {nav.map((item, index) => {
              const active = isActivePath(pathname, locale, item);
              return (
                <ChromeAnchor
                  ariaCurrent={active ? "page" : undefined}
                  className={cn(
                    "rounded-lg px-3 py-1.5 font-medium text-sm transition-colors hover:bg-accent hover:text-foreground",
                    active ? "text-foreground" : "text-muted-foreground",
                    // The last link is the first to go when space runs out.
                    index === nav.length - 1 && "md:hidden lg:inline-flex"
                  )}
                  key={item.href}
                  link={item}
                  locale={locale}
                />
              );
            })}
          </nav>

          {/* Utilities cluster: icon-only controls, then the one primary action. */}
          <div className="ms-auto flex items-center sm:gap-1">
            <Button
              aria-haspopup="dialog"
              aria-keyshortcuts="Control+K Meta+K"
              aria-label={dict.nav.search}
              className="text-muted-foreground hover:text-foreground"
              onClick={openCommandPalette}
              size="icon-sm"
              title={`${dict.nav.search} (⌘K)`}
              type="button"
              variant="ghost"
            >
              <Search aria-hidden="true" className="size-[18px]" />
            </Button>
            {/* Opens downwards, right-aligned: the header is pinned to the top of
                the viewport and this control sits at its right end. */}
            <LanguageSelector
              align="end"
              currentLocale={locale}
              englishPath={englishPath}
              iconOnly
              label={dict.footer.languageLabel}
              placement="bottom"
            />
            <ThemeToggle />

            <span
              aria-hidden="true"
              className="mx-1.5 hidden h-5 w-px bg-border sm:block"
            />

            <Button asChild className="hidden sm:inline-flex" size="sm">
              <Link href={bibliographyHref}>
                <BookOpen aria-hidden="true" className="size-4" />
                {dict.nav.bibliography}
                {badge}
              </Link>
            </Button>
            <Button
              asChild
              className="relative text-muted-foreground hover:text-foreground sm:hidden"
              size="icon-sm"
              variant="ghost"
            >
              <Link aria-label={dict.nav.bibliography} href={bibliographyHref}>
                <BookOpen aria-hidden="true" className="size-[18px]" />
                {citationCount > 0 ? (
                  <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-semibold text-[10px] text-primary-foreground tabular-nums">
                    {citationCount}
                  </span>
                ) : null}
              </Link>
            </Button>

            <Button
              aria-controls="site-menu"
              aria-expanded={menuOpen}
              aria-label={dict.nav.menu}
              className="text-muted-foreground hover:text-foreground md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              {menuOpen ? (
                <X aria-hidden="true" className="size-5" />
              ) : (
                <Menu aria-hidden="true" className="size-5" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu: every text link, plus the primary action in full. */}
      <div
        className={cn(
          "border-border/60 border-t bg-background md:hidden",
          menuOpen ? "block" : "hidden"
        )}
        id="site-menu"
      >
        <nav aria-label={dict.nav.menu} className="site-container py-3">
          <ul className="flex flex-col gap-0.5">
            {nav.map((item) => {
              const active = isActivePath(pathname, locale, item);
              return (
                <li key={item.href}>
                  <ChromeAnchor
                    ariaCurrent={active ? "page" : undefined}
                    className={cn(
                      "flex rounded-lg px-3 py-2.5 font-medium text-base transition-colors hover:bg-accent",
                      active
                        ? "bg-accent/60 text-foreground"
                        : "text-foreground/80"
                    )}
                    link={item}
                    locale={locale}
                    onClick={() => setMenuOpen(false)}
                  />
                </li>
              );
            })}
          </ul>
          <div className="mt-3 border-border/60 border-t pt-3">
            <Button asChild className="w-full" size="default">
              <Link href={bibliographyHref}>
                <BookOpen aria-hidden="true" className="size-4" />
                {dict.nav.bibliography}
                {badge}
              </Link>
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function FooterColumn({
  heading,
  locale,
  links,
}: {
  heading: string;
  locale: string;
  links: ChromeLink[];
}) {
  return (
    <div>
      <h3 className="mb-4 font-semibold text-foreground text-sm">{heading}</h3>
      <ul className="space-y-2.5 text-sm">
        {links.map((link) => (
          <li key={link.href}>
            <ChromeAnchor
              className="text-muted-foreground transition-colors duration-200 hover:text-foreground"
              link={link}
              locale={locale}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The five styles the footer promotes.
 *
 * Names only, no edition suffix. "APA 7th Edition" reads as an English string sitting
 * in Spanish prose, and the edition is on the style's own page anyway; the style name
 * is the part that carries the link's meaning in every language.
 */
const POPULAR_STYLES: ChromeLink[] = [
  { href: "/apa", label: "APA", localized: true },
  { href: "/mla", label: "MLA", localized: true },
  { href: "/chicago", label: "Chicago", localized: true },
  { href: "/vancouver", label: "Vancouver", localized: true },
  { href: "/harvard", label: "Harvard", localized: true },
];

const BOTTOM_LINK_CLASS =
  "transition-colors duration-200 hover:text-foreground";

export function SiteFooter({
  locale,
  dict,
  englishPath,
  styleCount,
}: {
  locale: string;
  dict: SiteChromeDictionary;
  englishPath: string;
  /** Preformatted for this locale — 10,834 in English, 10.834 in German. */
  styleCount: string;
}) {
  const styles: ChromeLink[] = [
    ...POPULAR_STYLES,
    { href: "/citation-styles", label: dict.footer.allStyles, localized: true },
    { href: "/compare", label: "Compare Styles" },
    { href: "/cheat-sheets", label: "Cheat Sheets" },
  ];

  const tools: ChromeLink[] = [
    { href: "/bibliography", label: dict.bib.studioTitle, localized: true },
    { href: "/tools", label: dict.footer.allTools, localized: true },
    { href: "/tools/word-counter", label: "Word Counter" },
    { href: "/tools/title-case-converter", label: "Title Case Converter" },
    { href: "/tools/readability-checker", label: "Readability Checker" },
    {
      href: "/tools/bibliography-alphabetizer",
      label: "Bibliography Alphabetizer",
    },
  ];
  if (locale === "en") {
    tools.push(
      { href: "/verify-bibliography", label: "Verify a Bibliography" },
      { href: "/tools/fake-citation-checker", label: "Fake Citation Checker" },
      { href: "/embed", label: "Embed Studio" }
    );
  }

  const resources: ChromeLink[] = [
    { href: "/how-to-cite", label: dict.footer.howToCiteAny, localized: true },
    { href: "/guides", label: dict.footer.citationGuides, localized: true },
    {
      href: "/glossary",
      label: dict.footer.citationGlossary,
      localized: true,
    },
    { href: "/faq", label: dict.hubs.faqTitle, localized: true },
    { href: "/quiz", label: "Style Quiz" },
    { href: "/search", label: "Search the Site" },
  ];

  const company: ChromeLink[] = [
    { href: "/about", label: dict.footer.about },
    { href: "/contact", label: dict.footer.contact },
    { href: "/ai", label: "API & MCP for AI Agents" },
  ];
  if (locale === "en") {
    company.push({
      href: "/for-educators",
      label: "For Educators & Librarians",
    });
  }

  const legal: ChromeLink[] = [
    { href: "/privacy", label: dict.footer.privacy },
    { href: "/terms", label: dict.footer.terms },
  ];

  return (
    <footer className="border-border/60 border-t bg-muted/30">
      <div className="site-container py-14 lg:py-16">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-12 lg:gap-8">
          {/* Brand column */}
          <div className="col-span-2 lg:col-span-4 lg:pe-8">
            <Wordmark locale={locale} />
            <p className="mt-4 max-w-xs text-muted-foreground text-sm leading-relaxed">
              {t(dict.footer.tagline, { count: styleCount })}
            </p>
            {/* Opens upwards (the footer is the last thing on the page) and
                start-aligned (this is the leftmost column, so a right-aligned
                panel grows off the edge of the screen). */}
            <div className="mt-6">
              <LanguageSelector
                align="start"
                currentLocale={locale}
                englishPath={englishPath}
                label={dict.footer.languageLabel}
                placement="top"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <FooterColumn
              heading={dict.footer.popularStyles}
              links={styles}
              locale={locale}
            />
          </div>
          <div className="lg:col-span-2">
            <FooterColumn
              heading={dict.footer.freeTools}
              links={tools}
              locale={locale}
            />
          </div>
          <div className="lg:col-span-2">
            <FooterColumn
              heading={dict.footer.resources}
              links={resources}
              locale={locale}
            />
          </div>
          <div className="lg:col-span-2">
            <FooterColumn
              heading={dict.footer.about}
              links={company}
              locale={locale}
            />
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-border/60 border-t pt-6 text-muted-foreground text-xs sm:flex-row sm:items-center sm:justify-between lg:mt-14">
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>
              {t(dict.footer.copyright, {
                year: String(new Date().getFullYear()),
              })}
            </span>
            {legal.map((link) => (
              <ChromeAnchor
                className={BOTTOM_LINK_CLASS}
                key={link.href}
                link={link}
                locale={locale}
              />
            ))}
          </p>
          <p>
            {dict.footer.poweredBy}{" "}
            <a
              className="underline decoration-border underline-offset-4 transition-colors duration-200 hover:text-foreground hover:decoration-foreground"
              href="https://github.com/citation-style-language/styles"
              rel="noopener noreferrer"
              target="_blank"
            >
              Citation Style Language
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
